import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { mdiCheck } from '@mdi/js'
import { Helmet } from 'react-helmet'

import { OrcidIdLink, Step } from '../components'
import { userFullName } from '../lib/user'
import orcidMemberLogo from '../img/ORCID_Member_Logo.svg'

import { Row, Col, Button } from 'react-bootstrap'
import { JCULogo, Icon } from '@jcu/cookbook-react'
import orcidIcon from 'simple-icons/icons/orcid'

import { ApplicationContext } from './App'

const Home = ({
  onLoginClick,
  onLogoutClick,
  onLinkOrcidClick,
  onCreateOrcidClick,
}) => {
  const { user, orcid } = useContext(ApplicationContext)

  const jcuLogo = (
    <JCULogo
      variant="horizontal"
      alt="JCU Australia logo"
      className="step-logo img-fluid center-block m-b-1"
    />
  )
  const orcidLogo = (
    <Icon
      path={orcidIcon.path}
      color={orcidIcon.hex}
      className="step-logo img-fluid center-block m-b-1"
      title="ORCiD Logo"
    />
  )

  const check = (
    <Icon
      className="step-logo img-fluid center-block m-b-1"
      path={mdiCheck}
      title="Done!"
      color="008a00"
    />
  )

  const fullName = userFullName(user)

  return (
    <>
      <Helmet>
        <title>Home</title>
      </Helmet>
      <Row className="mb-4">
        <Col md={8}>
          <p className="lead">
            Connect your JCU identity to your ORCID iD to verify your record. We
            never see or store any passwords during this process.{' '}
            <Link to="/about">Learn more</Link>
          </p>
        </Col>
        <Col md={4} className="text-center">
          <img
            className="img-fluid center-block"
            src={orcidMemberLogo}
            alt="ORCID Member Logo"
            style={{ width: '16rem' }}
          />
        </Col>
      </Row>
      <Row xs={1} md={3}>
        <Step name="Step 1" isAvailable isDone={!!user.sub}>
          <div className="mb-3 mb-md-4">
            {!user.sub ? (
              <a href="#login" onClick={onLoginClick}>
                {jcuLogo}
              </a>
            ) : (
              jcuLogo
            )}
          </div>
          {!user.sub && (
            <Row className="justify-content-center">
              <Col xs={12} md={10}>
                <div className="d-grid">
                  <Button
                    size="lg"
                    variant="outline-primary"
                    className="text-wrap"
                    onClick={onLoginClick}
                  >
                    Sign in with JCU
                  </Button>
                </div>
              </Col>
            </Row>
          )}

          {user.sub && (
            <>
              <p className="lead mb-1">
                Signed in as
                {fullName && (
                  <>
                    <br />
                    <strong>{fullName}</strong>
                  </>
                )}
                {user.sub && (
                  <>
                    <br />
                    {user.sub}
                  </>
                )}
              </p>
              <p className="text-muted">
                Not you?{' '}
                <a href="#logout" onClick={onLogoutClick}>
                  Log out
                </a>
              </p>
            </>
          )}
        </Step>
        {/* end of step 1 */}
        <Step name="Step 2" isAvailable={!!user.sub} isDone={!!orcid.orcid_id}>
          <div className="mb-3 mb-md-4">
            {!orcid.orcid_id ? (
              <a href="#orcid-link" onClick={onLinkOrcidClick}>
                {orcidLogo}
              </a>
            ) : (
              orcidLogo
            )}
          </div>

          {!orcid.orcid_id && (
            <Row className="justify-content-center">
              <Col xs={12} md={10}>
                <div className="d-grid mb-2">
                  <Button
                    size="lg"
                    variant="outline-primary"
                    className="text-wrap"
                    onClick={onLinkOrcidClick}
                  >
                    Link your ORCID iD
                  </Button>
                </div>

                <p className="text-muted">
                  If you don&apos;t have an ORCID record, you can{' '}
                  <a href="#orcid-create" onClick={onCreateOrcidClick}>
                    create one
                  </a>
                  .
                </p>
              </Col>
            </Row>
          )}

          {orcid.orcid_id && (
            <p className="lead text-centerm">
              Linked to
              <br />
              <OrcidIdLink
                orcidId={orcid.orcid_id}
                sandbox={orcid.sandbox}
                showIcon={false}
              />
            </p>
          )}
        </Step>
        {/* end of step 2 */}

        <Step
          name="Done!"
          showDivider={false}
          isAvailable={!!(user.sub && orcid.orcid_id)}
        >
          <div className="mb-3 mb-md-4">{check}</div>

          <Row className="justify-content-center">
            <Col xs={12} md={10}>
              <div className="d-grid mb-2">
                <Button
                  size="lg"
                  variant="outline-primary"
                  className="text-wrap"
                  as={Link}
                  to="/settings"
                >
                  Configure settings
                </Button>
              </div>

              <p className="text-muted">
                You can manage or update your ORCID record link at any time.
              </p>
            </Col>
          </Row>
        </Step>
        {/* end of step 3 */}
      </Row>
      {/* TODO Add a "redirect to requester" here if we're doing SSO-style creation/linkage */}
    </>
  )
}

Home.propTypes = {
  onLoginClick: PropTypes.func.isRequired,
  onLogoutClick: PropTypes.func.isRequired,
  onLinkOrcidClick: PropTypes.func.isRequired,
  onCreateOrcidClick: PropTypes.func.isRequired,
}
export default Home
